/**
 * XPulseTicker — one-line social heartbeat for the Stage.
 *
 * X mentions are excluded from Live Scoring (they swamped it), so this
 * compact pill keeps the pulse visible: "🐦 6.8k mentions/hr · Elon Musk
 * leads". Data from GET /api/scoring/x-pulse (5-min server cache).
 * Hidden entirely when there's no mention activity today.
 */
import { useEffect, useState } from 'react';
import api from '../lib/api';

const fmtCount = (n) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return `${n}`;
};

export default function XPulseTicker({ onEntityClick }) {
  const [pulse, setPulse] = useState(null);
  const [beat, setBeat] = useState(0); // bump on each fresh fetch → re-trigger the pulse

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      api.get('/scoring/x-pulse')
        .then((res) => { if (!cancelled) { setPulse(res.data); setBeat((b) => b + 1); } })
        .catch(() => { if (!cancelled) setPulse(null); });
    };
    load();
    // 2026-07-23 — poll every 60s (endpoint is server-cached 90s) so the
    // "live heartbeat" recent_5m number visibly moves during a session.
    const interval = setInterval(load, 60 * 1000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  if (!pulse || !pulse.total_today) return null;

  const leader = pulse.leader;
  const canClick = !!(onEntityClick && leader?.entity_id);
  const recent = pulse.recent_5m || 0;
  return (
    <div
      className="mt-4 flex items-center gap-2 px-3 py-2 rounded-xl border border-border/60 bg-card text-xs"
      data-testid="x-pulse-ticker"
    >
      {/* Live heartbeat dot — pings while the pipeline is delivering mentions */}
      <span className="relative flex h-2 w-2 shrink-0" aria-hidden data-testid="x-pulse-live-dot">
        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
      </span>
      <span aria-hidden className="text-sm leading-none">🐦</span>
      <span className="font-semibold tabular-nums" data-testid="x-pulse-rate">
        {fmtCount(pulse.per_hour)} mentions/hr
      </span>
      {recent > 0 && (
        <span
          key={beat}
          className="px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-semibold tabular-nums animate-in fade-in zoom-in duration-500"
          data-testid="x-pulse-recent"
        >
          +{fmtCount(recent)} now
        </span>
      )}
      {leader && (
        <>
          <span className="text-muted-foreground">·</span>
          <span
            className={`font-medium truncate ${canClick ? 'cursor-pointer hover:underline' : ''}`}
            onClick={canClick ? () => onEntityClick(leader.entity_id) : undefined}
            role={canClick ? 'button' : undefined}
            data-testid="x-pulse-leader"
          >
            {leader.name} leads
          </span>
        </>
      )}
      <span className="ml-auto text-[10px] text-muted-foreground tabular-nums whitespace-nowrap">
        {fmtCount(pulse.total_today)} today
      </span>
    </div>
  );
}
